import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Network, Edit2, Trash2, Briefcase, Loader2, AlertCircle, UserPlus, FolderPlus, Shield } from 'lucide-react';
import { Plus, ChevronRight, ChevronDown, Users, X, User } from './icons';
import { areasAPI, authAPI, usuariosAPI } from '../services/api';
import { useToast } from '../contexts/ToastContext';
import GenericDeleteModal from './GenericDeleteModal';
import UserAutoComplete from './UserAutoComplete';
import OrgChart from './OrgChart';

const AreaItem = ({ area, level = 0, onRefresh, onEdit, onAddSubArea, onAssignUser, onEditCargo, onDeleteArea, onDeleteCargo }) => {
    const [isExpanded, setIsExpanded] = useState(level < 2);
    const hasChildren = area.children && area.children.length > 0;
    const hasCargos = area.cargos && area.cargos.length > 0;

    return (
        <div className="mb-2">
            <div className={`flex items-center group bg-white dark:bg-gray-800 rounded-lg border border-gray-100 dark:border-gray-700 hover:border-blue-200 dark:hover:border-blue-800 transition-all p-2 pr-4 ${level > 0 ? 'ml-6' : ''}`}>
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className={`p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors ${!hasChildren && !hasCargos ? 'invisible' : ''}`}
                >
                    {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
                </button>

                <div className="flex-1 flex items-center gap-3 ml-1">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${area.tipoArea === 'Directorio' ? 'bg-purple-100 text-purple-600' :
                        area.tipoArea === 'Gerencia' ? 'bg-blue-100 text-blue-600' :
                            area.tipoArea === 'Subgerencia' ? 'bg-indigo-100 text-indigo-600' :
                                'bg-gray-100 text-gray-600'
                        }`}>
                        <Network className="w-4 h-4" />
                    </div>
                    <div>
                        <h4 className="text-sm font-semibold text-gray-800 dark:text-white">
                            {area.nombre}
                        </h4>
                        <p className="text-[10px] font-medium uppercase tracking-wider text-gray-400">
                            {area.tipoArea}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                        onClick={() => onAddSubArea(area)}
                        className="p-1.5 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-md transition-colors"
                        title="Añadir Sub-área"
                    >
                        <FolderPlus className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onAssignUser(area)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors"
                        title="Asignar Usuario"
                    >
                        <UserPlus className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onEdit(area)}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-md transition-colors"
                        title="Editar Área"
                    >
                        <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => onDeleteArea(area)}
                        className="p-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
                        title="Eliminar Área"
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {isExpanded && (
                <div className="mt-2 space-y-2">
                    {/* Cargos / Personas en esta área */}
                    {hasCargos && (
                        <div className={`space-y-1 ${level >= 0 ? 'ml-8' : 'ml-6'}`}>
                            {area.cargos.map(cargo => (
                                <div key={cargo.id} className="flex items-center group bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-2 pr-3 border border-transparent hover:border-gray-200 dark:hover:border-gray-600 transition-all">
                                    <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-[10px] text-white font-bold mr-3 shadow-sm">
                                        {cargo.usuario?.nombre?.[0]}{cargo.usuario?.apellido?.[0]}
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                                            {cargo.usuario?.nombre} {cargo.usuario?.apellido}
                                        </p>
                                        <div className="flex items-center gap-1.5">
                                            <p className="text-[10px] text-gray-500">
                                                {cargo.cargo}
                                            </p>
                                            {cargo.esJefatura && (
                                                <span className="flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-[8px] font-bold uppercase tracking-wider">
                                                    <Briefcase className="w-2 h-2" /> Jefatura
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={() => onEditCargo(area, cargo)}
                                            className="p-1 text-gray-400 hover:text-amber-600 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded transition-colors"
                                            title="Editar Cargo"
                                        >
                                            <Edit2 className="w-3 h-3" />
                                        </button>
                                        <button
                                            onClick={() => onDeleteCargo(cargo)}
                                            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                                            title="Eliminar Cargo"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Sub-áreas */}
                    {hasChildren && area.children.map(child => (
                        <AreaItem
                            key={child.id}
                            area={child}
                            level={level + 1}
                            onRefresh={onRefresh}
                            onEdit={onEdit}
                            onAddSubArea={onAddSubArea}
                            onAssignUser={onAssignUser}
                            onEditCargo={onEditCargo}
                            onDeleteArea={onDeleteArea}
                            onDeleteCargo={onDeleteCargo}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default function HierarchyManager() {
    const { showSuccess, showError } = useToast();
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [allUsers, setAllUsers] = useState([]);
    const [vista, setVista] = useState('organigrama'); // 'organigrama' | 'lista'

    // Modales
    const [areaModal, setAreaModal] = useState({ isOpen: false, area: null, parent: null });
    const [cargoModal, setCargoModal] = useState({ isOpen: false, cargo: null, area: null });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, type: null, target: null });

    const fetchTree = useCallback(async (showLoader = false) => {
        try {
            if (showLoader) setLoading(true);
            const response = await areasAPI.getTree();
            setTree(response.data);
        } catch (error) {
            console.error('Error fetching tree:', error);
            showError('No se pudo cargar el organigrama');
        } finally {
            if (showLoader) setLoading(false);
        }
    }, [showError]);

    const fetchUsers = useCallback(async () => {
        try {
            const response = await usuariosAPI.getAllNomina();
            setAllUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    }, []);

    useEffect(() => {
        fetchTree(true);
        fetchUsers();
    }, [fetchTree, fetchUsers]);

    const handleSaveArea = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const data = {
            nombre: formData.get('nombre'),
            tipoArea: formData.get('tipoArea'),
            idAreaPadre: areaModal.parent?.id || areaModal.area?.idAreaPadre || null
        };

        try {
            if (areaModal.area) {
                await areasAPI.update(areaModal.area.id, data);
                showSuccess('Área actualizada correctamente');
            } else {
                await areasAPI.create(data);
                showSuccess('Área creada correctamente');
            }
            setAreaModal({ isOpen: false, area: null, parent: null });
            fetchTree();
        } catch (error) {
            showError(error.response?.data?.message || 'Error al guardar el área');
        }
    };

    const handleSaveCargo = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);
        const cargo = formData.get('cargo');
        const esJefatura = formData.get('esJefatura') === 'on';

        try {
            if (cargoModal.cargo) {
                // UpdateCargoUsuarioDto no admite usuarioId (no se puede reasignar el
                // usuario de un cargo ya existente) -- mandarlo igual dispara 400
                // (forbidNonWhitelisted) por ser un campo no declarado en el DTO.
                await areasAPI.updateCargo(cargoModal.cargo.id, { areaId: cargoModal.area?.id, cargo, esJefatura });
                showSuccess('Cargo actualizado');
            } else {
                const data = {
                    usuarioId: parseInt(formData.get('usuarioId')),
                    areaId: cargoModal.area?.id,
                    cargo,
                    esJefatura
                };
                await areasAPI.createCargo(data);
                showSuccess('Usuario asignado');
            }
            setCargoModal({ isOpen: false, cargo: null, area: null });
            fetchTree();
        } catch (error) {
            showError(error.response?.data?.message || 'Error al guardar cargo');
        }
    };

    const handleDelete = async () => {
        if (!deleteModal.target) return;

        try {
            if (deleteModal.type === 'area') {
                await areasAPI.delete(deleteModal.target.id);
                showSuccess('Área eliminada');
            } else {
                await areasAPI.deleteCargo(deleteModal.target.id);
                showSuccess('Cargo eliminado');
            }
            fetchTree();
            setDeleteModal({ isOpen: false, type: null, target: null });
        } catch (error) {
            showError(error.response?.data?.message || 'Error al eliminar');
        }
    };

    /* if (loading) return (
        <div className="flex flex-col items-center justify-center p-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
            <p className="text-gray-500 text-sm">Cargando organigrama...</p>
        </div>
    ); */

    return (
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                    <Network className="w-5 h-5 text-blue-500" />
                    <h3 className="font-bold text-gray-800 dark:text-gray-100">Organigrama (Actualizado)</h3>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg p-1 border border-gray-200 dark:border-gray-700">
                        <button
                            onClick={() => setVista('organigrama')}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${vista === 'organigrama'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                        >
                            Organigrama
                        </button>
                        <button
                            onClick={() => setVista('lista')}
                            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors ${vista === 'lista'
                                ? 'bg-white dark:bg-gray-700 text-blue-600 dark:text-blue-400 shadow-xs'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                        >
                            Lista
                        </button>
                    </div>
                    <button
                        onClick={() => setAreaModal({ isOpen: true, area: null, parent: null })}
                        className="p-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors shadow-sm"
                        title="Nueva Área Raíz"
                    >
                        <Plus className="w-5 h-5" />
                    </button>
                </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(100vh-300px)] custom-scrollbar relative">
                {loading && tree.length === 0 ? (
                    <div className="flex flex-col items-center justify-center p-12">
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mb-3" />
                        <p className="text-gray-500 text-sm">Cargando organigrama...</p>
                    </div>
                ) : tree.length === 0 ? (
                    <div className="text-center py-12">
                        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">Aún no se han definido áreas</p>
                        <button
                            onClick={() => setAreaModal({ isOpen: true, area: null, parent: null })}
                            className="mt-4 text-blue-600 text-sm font-semibold hover:underline"
                        >
                            Comenzar ahora
                        </button>
                    </div>
                ) : (
                    <>
                        {loading && (
                            <div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-10 flex items-center justify-center backdrop-blur-[1px]">
                                <Loader2 className="w-6 h-6 text-blue-500 animate-spin mr-2" />
                                <span className="text-xs font-medium text-gray-500">Actualizando...</span>
                            </div>
                        )}
                        {vista === 'organigrama' ? (
                            <OrgChart
                                tree={tree}
                                allUsers={allUsers}
                                onEditCargo={(a, c) => setCargoModal({ isOpen: true, cargo: c, area: a })}
                            />
                        ) : (
                            tree.map(area => (
                                <AreaItem
                                    key={area.id}
                                    area={area}
                                    onRefresh={fetchTree}
                                    onEdit={(a) => setAreaModal({ isOpen: true, area: a, parent: null })}
                                    onAddSubArea={(p) => setAreaModal({ isOpen: true, area: null, parent: p })}
                                    onAssignUser={(a) => setCargoModal({ isOpen: true, cargo: null, area: a })}
                                    onEditCargo={(a, c) => setCargoModal({ isOpen: true, cargo: c, area: a })}
                                    onDeleteArea={(a) => setDeleteModal({ isOpen: true, type: 'area', target: a })}
                                    onDeleteCargo={(c) => setDeleteModal({ isOpen: true, type: 'cargo', target: c })}
                                />
                            ))
                        )}
                    </>
                )}
            </div>

            {/* Modal de Área */}
            {areaModal.isOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800 dark:text-white">
                                {areaModal.area ? 'Editar Área' : 'Nueva Área'}
                                {areaModal.parent && <span className="text-gray-400 text-sm block">Bajo: {areaModal.parent.nombre}</span>}
                            </h3>
                            <button onClick={() => setAreaModal({ isOpen: false })} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveArea} className="p-6 space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Nombre</label>
                                <input
                                    required
                                    name="nombre"
                                    defaultValue={areaModal.area?.nombre}
                                    placeholder="Ej: Departamento TI"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Tipo de Área</label>
                                <select
                                    name="tipoArea"
                                    defaultValue={areaModal.area?.tipoArea || 'Departamento'}
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 outline-none appearance-none cursor-pointer"
                                >
                                    <option value="Directorio">Directorio</option>
                                    <option value="Gerencia">Gerencia</option>
                                    <option value="Subgerencia">Subgerencia</option>
                                    <option value="Departamento">Departamento</option>
                                    <option value="Equipo">Equipo</option>
                                </select>
                            </div>
                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setAreaModal({ isOpen: false })} className="flex-1 px-4 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-transform active:scale-95 shadow-lg shadow-blue-500/30">
                                    Guardar
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal de Cargo */}
            {cargoModal.isOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                        <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
                            <h3 className="font-bold text-gray-800 dark:text-white">
                                {cargoModal.cargo ? 'Editar Cargo' : 'Asignar Usuario'}
                                <span className="text-blue-500 text-sm block">{cargoModal.area?.nombre}</span>
                            </h3>
                            <button onClick={() => setCargoModal({ isOpen: false })} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <form onSubmit={handleSaveCargo} className="p-6 space-y-4">
                            {!cargoModal.cargo && (
                                <UserAutoComplete
                                    value={cargoModal.selectedUserId || ''}
                                    onChange={(userId) => {
                                        const user = allUsers.find(u => u.id === userId);
                                        if (user) {
                                            // Update hidden input if needed or state
                                            // But crucially, auto-fill cargo
                                            if (user.cargo) {
                                                const cargoInput = document.querySelector('input[name="cargo"]');
                                                if (cargoInput) cargoInput.value = user.cargo;
                                            }
                                            // Handle the form data linkage manually since UserAutoComplete doesn't bubble name
                                            // actually UserAutoComplete calls onChange with the ID
                                            // We need to inject a hidden input for the form submit to work naturally
                                            // OR handle state. Let's use a hidden input for the ID
                                            setCargoModal(prev => ({ ...prev, selectedUserId: userId }));
                                        } else {
                                            setCargoModal(prev => ({ ...prev, selectedUserId: null }));
                                        }
                                    }}
                                    usuarios={allUsers}
                                    placeholder="Buscar usuario por nombre, departamento o email..."
                                    label="Seleccionar Usuario"
                                    required={true}
                                />
                            )}
                            {/* Hidden input to ensure form submission works if using FormData directly from form elements */}
                            {!cargoModal.cargo && (
                                <input type="hidden" name="usuarioId" value={cargoModal.selectedUserId || ''} />
                            )}
                            {cargoModal.cargo && (
                                <div>
                                    <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Usuario</label>
                                    <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 text-sm font-medium">
                                        {cargoModal.cargo.usuario?.nombre} {cargoModal.cargo.usuario?.apellido}
                                        <input type="hidden" name="usuarioId" value={cargoModal.cargo.usuarioId} />
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-xs font-bold text-gray-400 uppercase mb-2 ml-1">Nombre del Cargo</label>
                                <input
                                    required
                                    name="cargo"
                                    defaultValue={cargoModal.cargo?.cargo}
                                    placeholder="Ej: Jefe de Proyectos"
                                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 focus:ring-2 focus:ring-blue-500 outline-none transition-all"
                                />
                            </div>
                            <label className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-700 cursor-pointer">
                                <input
                                    type="checkbox"
                                    name="esJefatura"
                                    defaultChecked={cargoModal.cargo?.esJefatura}
                                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                />
                                <div className="flex-1">
                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-200 uppercase">Es Jefatura</p>
                                    <p className="text-[10px] text-gray-500">¿Tiene subordinados a su cargo?</p>
                                </div>
                                <Shield className="w-4 h-4 text-amber-500" />
                            </label>

                            <div className="pt-4 flex gap-3">
                                <button type="button" onClick={() => setCargoModal({ isOpen: false })} className="flex-1 px-4 py-2.5 rounded-xl text-gray-600 dark:text-gray-400 font-bold hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                                    Cancelar
                                </button>
                                <button type="submit" className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-bold hover:bg-blue-700 transition-transform active:scale-95 shadow-lg shadow-blue-500/30">
                                    {cargoModal.cargo ? 'Guardar' : 'Asignar'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}

            <GenericDeleteModal
                isOpen={deleteModal.isOpen}
                onClose={() => setDeleteModal({ isOpen: false, type: null, target: null })}
                onConfirm={handleDelete}
                title={`Eliminar ${deleteModal.type === 'area' ? 'Área' : 'Cargo'}`}
                message={`¿Estás seguro de que deseas eliminar ${deleteModal.type === 'area' ? 'esta área y todas sus subáreas' : 'este cargo'}?`}
            />
        </div>
    );
}
